import { ElementRef, EventEmitter, ViewChild, OnDestroy, AfterViewInit } from "@angular/core";
import { Component, OnInit, Output } from "@angular/core";
import { VoiceInputComponent } from "../voice-input/voice-input.component";
import { Platform } from "@ionic/angular";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

@Component({
  selector: "app-messagerie-text-input",
  templateUrl: "./messagerie-text-input.component.html",
  styleUrls: ["./messagerie-text-input.component.scss"],
  standalone: false,
})
export class MessagerieTextInputComponent implements OnInit, AfterViewInit, OnDestroy {
  inputType: string = "text";
  buttonType: string = "vocal";
  recording: boolean = false;
  message: string = "";
  voiceMessage: string = "";
  extension: string = "";
  duration: number = 0;
  private viewportResizeHandler?: () => void;
  private keyboardWillShowListener?: any;
  private keyboardWillHideListener?: any;
  private inputWrapper?: HTMLElement;

  @Output() send: EventEmitter<any> = new EventEmitter<any>();
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() recordingStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild("messageInput") messageInputRef!: ElementRef;
  @ViewChild("inputWrapper") inputWrapperRef!: ElementRef;
  @ViewChild(VoiceInputComponent) childComponent!: VoiceInputComponent;

  constructor(private platform: Platform) {}

  ngOnInit() {}
  
  ngAfterViewInit() {
    // Setup input positioning for iOS keyboard
    if (this.platform.is('ios')) {
      this.setupIOSKeyboardPositioning();
    }
  }
  
  ngOnDestroy() {
    // Clean up viewport listeners
    if (this.viewportResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
    }
    // Clean up Capacitor keyboard listeners
    if (this.keyboardWillShowListener) {
      this.keyboardWillShowListener.remove();
    }
    if (this.keyboardWillHideListener) {
      this.keyboardWillHideListener.remove();
    }
  }
  
  private setupIOSKeyboardPositioning(): void {
    // Get the input wrapper element from ViewChild
    if (this.inputWrapperRef && this.inputWrapperRef.nativeElement) {
      this.inputWrapper = this.inputWrapperRef.nativeElement;
    } else {
      // Fallback: query DOM if ViewChild not available yet
      this.inputWrapper = document.querySelector('.message-input-wrapper') as HTMLElement;
    }
    
    if (!this.inputWrapper) {
      // Retry after a short delay
      setTimeout(() => this.setupIOSKeyboardPositioning(), 200);
      return;
    }
    
    // Get safe area
    const safeArea = this.getSafeAreaBottom();
    
    // For native iOS with KeyboardResize.Ionic the web view resizes when keyboard opens,
    // so the bar should stay at bottom: safeArea (viewport already shrunk). Do NOT add keyboardHeight
    // or the bar jumps to the top.
    if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
      this.keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', () => {
        if (this.inputWrapper) {
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
        }
      });
      
      this.keyboardWillHideListener = Keyboard.addListener('keyboardWillHide', () => {
        if (this.inputWrapper) {
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
        }
      });
      
      this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
    } else if (window.visualViewport) {
      // For iOS web, use Visual Viewport API
      let initialViewportHeight = window.visualViewport.height;
      
      this.viewportResizeHandler = () => {
        if (!this.inputWrapper) return;
        
        const viewport = window.visualViewport!;
        const viewportHeight = viewport.height;
        // Calculate keyboard height: difference between initial and current viewport
        const keyboardHeight = initialViewportHeight - viewportHeight;
        
        if (keyboardHeight > 50) {
          // Keyboard is open - position input above keyboard
          this.inputWrapper.style.setProperty('bottom', `${keyboardHeight + safeArea}px`, 'important');
        } else {
          // Keyboard is closed - position at bottom with safe area
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
          // Update initial height when keyboard closes
          initialViewportHeight = viewportHeight;
        }
      };
      
      window.visualViewport.addEventListener('resize', this.viewportResizeHandler);
      // Initial positioning
      setTimeout(() => {
        if (this.inputWrapper) {
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
        }
      }, 100);
    } else {
      // Fallback: listen to window resize
      let initialViewportHeight = window.innerHeight;
      
      const handleResize = () => {
        if (!this.inputWrapper) return;
        
        const currentViewportHeight = window.innerHeight;
        const keyboardHeight = Math.max(initialViewportHeight - currentViewportHeight, 0);
        
        if (keyboardHeight > 50) {
          this.inputWrapper.style.setProperty('bottom', `${keyboardHeight + safeArea}px`, 'important');
        } else {
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
          // Update initial height when keyboard closes
          initialViewportHeight = currentViewportHeight;
        }
      };
      
      window.addEventListener('resize', handleResize);
      setTimeout(() => {
        if (this.inputWrapper) {
          this.inputWrapper.style.setProperty('bottom', `${safeArea}px`, 'important');
        }
      }, 100);
    }
  }
  
  private getSafeAreaBottom(): number {
    try {
      const safeArea = getComputedStyle(document.documentElement)
        .getPropertyValue('env(safe-area-inset-bottom)') || 
        getComputedStyle(document.documentElement)
        .getPropertyValue('constant(safe-area-inset-bottom)') || '0px';
      return parseInt(safeArea.replace('px', '')) || 0;
    } catch (e) {
      return 0;
    }
  }
  messageInputChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      this.onChange.emit(this.message);
      if (!this.message.length) {
        this.inputType = "vocal";
        this.buttonType = "vocal";
      }else{
        this.inputType = "text";
        this.buttonType = "text";
      }
    }
  }
  onTextInputFocus() {
    this.inputType = "text";
    this.buttonType = "text";
    // Emit focus event to parent component
    this.inputFocus.emit();
    
    // Ensure input is positioned correctly when focused (iOS)
    if (this.platform.is('ios') && this.inputWrapper) {
      // Trigger viewport resize handler if available
      if (this.viewportResizeHandler) {
        setTimeout(() => {
          this.viewportResizeHandler?.();
        }, 100);
      }
      // For native, the keyboard listeners will handle it automatically
    }
  }
  onTextInputBlur() {
    if (!this.message.length) {
      this.inputType = "vocal";
      this.buttonType = "vocal";
    }
  }
  sendMessage() {
    const messageData = {
      message: this.message,
      voiceMessage: this.voiceMessage,
      duration: this.duration,
      extension: this.extension,
    };
    this.send.emit(messageData);
    setTimeout(() => {
      this.resetInput();
      this.buttonType = "vocal"

    }, 1000);
  }
  sendVoiceMessage(data: any) {
    this.voiceMessage = data.voiceMessage;
    this.duration = data.duration;
    this.extension = data.extension;
    this.sendMessage();
    setTimeout(() => {
      this.focusOnInput();
      this.buttonType = "vocal"
    }, 1000);
  }

  focusOnInput() {
    this.messageInputRef && this.messageInputRef.nativeElement.focus();
  }
  resetInput() {
    this.messageInputRef && (this.messageInputRef.nativeElement.value = "");
    this.message && (this.message = "");
    this.voiceMessage && (this.voiceMessage = "");
    this.duration && (this.duration = 0);
  }
  startRecording() {
    this.buttonType = "vocal";
    this.recording = true;
    this.recordingStateChange.emit(true);
  }

  cancelRecords() {
    this.buttonType = "vocal";
    this.recording = false;
    this.recordingStateChange.emit(false);
  }

  forceStopRecording(): void {
    if (!this.recording) {
      return;
    }
    this.recording = false;
    this.recordingStateChange.emit(false);
    if (this.childComponent) {
      this.childComponent.deleteRecording();
    }
  }

}
